import { message, notification } from 'antd';
import apiClient from '@/utils/apiClient';
import { addCalComCalendar } from '@/api/calendars/calcom';

/**
 * GoHighLevelIntegration Component
 *
 * This component contains utility functions for GoHighLevel OAuth integration
 * to be used within the ApiWorkflowTemplate components.
 */

export interface GhlCalendar {
	id: string;
	name: string;
}

/**
 * Checks the connection status of GoHighLevel integration
 * @param resellerEmailCheck - The reseller check value ('RESELLER' or 'RECEPTIONIST')
 * @returns Connection status response
 */
export const checkGoHighLevelConnection = async ({
	resellerEmailCheck,
	agentId,
}: {
	resellerEmailCheck: string;
	agentId: string;
}) => {
	try {
		const response = await apiClient.request({
			method: 'GET',
			endPoint: '/api/v1/gohighlevel/agent/:agentId/status'.replace(':agentId', agentId),
			params: { resellerEmailCheck },
		});
		return response;
	} catch (error) {
		console.error('Error checking GoHighLevel connection:', error);
		notification.error({
			message: 'Connection Check Failed',
			description: 'Unable to verify GoHighLevel connection status.',
		});
		return { isConnected: false };
	}
};

/**
 * Opens the OAuth authorization URL in a popup window or the current window
 * @param authUrl - The authorization URL to navigate to
 * @param openInNewTab - Whether to open in a new tab/popup (default: true)
 */
export const openOAuthWindow = (
	authUrl: string,
	openInNewTab: boolean = true,
	source: string = 'generic',
) => {
	const urlWithSource = new URL(authUrl);
	urlWithSource.searchParams.append('source', source);
	const finalAuthUrl = urlWithSource.toString();

	if (openInNewTab) {
		const width = 600;
		const height = 700;
		const left = window.screen.width / 2 - width / 2;
		const top = window.screen.height / 2 - height / 2;

		const popup = window.open(
			finalAuthUrl,
			'oauth-popup',
			`width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`,
		);

		if (!popup) {
			window.open(finalAuthUrl, '_blank', 'noopener,noreferrer');
			message.warning('Popup was blocked. Please allow popups for this site.');
			return;
		}

		const checkPopupClosed = setInterval(() => {
			if (popup?.closed) {
				clearInterval(checkPopupClosed);
				window.dispatchEvent(new CustomEvent('oauth-complete', { detail: { source } }));
			}
		}, 500);

		const handleAuthMessage = (event: MessageEvent) => {
			if (event.data?.type === 'oauth-complete' && event.data.source === source) {
				window.removeEventListener('message', handleAuthMessage);
				clearInterval(checkPopupClosed);
				if (!popup.closed) {
					popup.close();
				}
				window.dispatchEvent(
					new CustomEvent('oauth-complete', { detail: { source, success: true } }),
				);
			}
		};

		window.addEventListener('message', handleAuthMessage);

		return () => {
			window.removeEventListener('message', handleAuthMessage);
			clearInterval(checkPopupClosed);
			if (popup && !popup.closed) {
				popup.close();
			}
		};
	} else {
		window.location.href = finalAuthUrl;
	}
};

/**
 * Initiates the GoHighLevel OAuth authorization process
 * @param username - The user's username/number
 * @param resellerEmailCheck - The reseller check value ('RESELLER' or 'RECEPTIONIST')
 * @param redirectURL - The callback URL for OAuth
 * @param state - The state parameter for OAuth
 * @returns Authorization URL
 */
export const getGoHighLevelAuthUrl = async (
	username: string,
	resellerEmailCheck: string,
	redirectURL: string,
	state: string,
) => {
	try {
		const response = await apiClient.request({
			method: 'GET',
			endPoint: '/api/v1/gohighlevel/auth/url',
			params: { username, state, resellerEmailCheck, redirectURL },
		});

		if (!response.authorize_url) {
			throw new Error('No authorization URL returned');
		}

		return response.authorize_url;
	} catch (error) {
		console.error('Failed to get GoHighLevel authorization URL:', error);

		// If we got a 404, show a specific message
		if ((error as any)?.response?.status === 404) {
			notification.error({
				message: 'Integration Not Available',
				description:
					'The GoHighLevel integration endpoint is not available. Please contact support.',
			});
		}

		throw error;
	}
};

/**
 * Processes the OAuth callback from GoHighLevel
 * @param code - The authorization code from OAuth callback
 * @param resellerEmailCheck - The reseller check value ('RESELLER' or 'RECEPTIONIST')
 * @param redirectURL - The callback URL used for OAuth
 * @returns Response from the callback endpoint
 */
export const processGoHighLevelCallback = async (
	code: string,
	resellerEmailCheck: string,
	redirectURL: string,
) => {
	try {
		const response = await apiClient.request({
			method: 'GET',
			endPoint: '/api/v1/gohighlevel/auth/callback',
			params: { code, resellerEmailCheck, redirectURL },
		});
		return response;
	} catch (error) {
		console.error('Error processing GoHighLevel callback:', error);
		message.error('An error occurred during the OAuth callback process.');
		throw error;
	}
};

/**
 * Disconnects from GoHighLevel integration
 * @param username - The user's username/number
 * @returns Response from disconnect endpoint
 */
export const disconnectGoHighLevel = async (username: string, agentId: string) => {
	try {
		const response = await apiClient.request({
			method: 'POST',
			endPoint: '/api/v1/gohighlevel/agent/:agentId/disconnect'.replace(':agentId', agentId),
			body: { username },
		});
		return response;
	} catch (error) {
		console.error('Error disconnecting from GoHighLevel:', error);
		message.error('Failed to disconnect from GoHighLevel');
		throw error;
	}
};

/**
 * Creates the OAuth state parameter for GoHighLevel
 * @param username - The user's username/number
 * @param resellerEmailCheck - The reseller check value ('RESELLER' or 'RECEPTIONIST')
 * @param path - The current path to return to after OAuth
 * @param searchParams - The search parameters to preserve
 * @returns Encoded state string
 */
export const createGoHighLevelOAuthState = (
	username: string,
	resellerEmailCheck: string,
	path: string = window.location.pathname,
	searchParams: URLSearchParams = new URLSearchParams(window.location.search),
) => {
	return btoa(
		JSON.stringify({
			domain: window.location.hostname,
			port: window.location.port,
			protocol: window.location.protocol.replace(':', ''),
			path,
			query: searchParams.toString(),
			username,
			resellerEmailCheck,
		}),
	);
};

/**
 * Fetches the list of calendars from GoHighLevel
 * @returns List of calendars
 */
export const fetchGhlCalendars = async ({
	resellerEmailCheck,
	agentId,
}: {
	resellerEmailCheck: string;
	agentId: string;
}) => {
	try {
		const response = await apiClient.request({
			method: 'GET',
			endPoint: '/api/v1/gohighlevel/agent/:agentId/calendars'.replace(':agentId', agentId),
			params: { resellerEmailCheck },
		});

		return response.calendars || [];
	} catch (error) {
		console.error('Error fetching GoHighLevel calendars:', error);
		message.error('Failed to fetch calendars from GoHighLevel');
		return [];
	}
};

/**
 * Creates a calendar workflow for GoHighLevel
 * @param selectedCalendar - The selected calendar ID
 * @param selectedCalendarName - The selected calendar name
 * @param resellerEmailCheck - The reseller check value ('RESELLER' or 'RECEPTIONIST')
 * @returns Response from the calendar creation endpoint
 */
export const createGhlCalendarWorkflow = async (
	selectedCalendar: string,
	selectedCalendarName: string,
	username: string,
) => {
	if (!selectedCalendar || !selectedCalendarName) {
		message.error('Please select a calendar');
		throw new Error('Calendar selection is required');
	}

	const newCalendar = {
		eventTypeId: selectedCalendar,
		scenario: 'Whenever the caller wants to book or schedule any meetings or appointments.',
		sendCallerSMSNotification: true,
		customFields: [],
		nameOfCalendar: 'gohighlevel',
		sendOwnerSMSNotification: true,
		location: null,
		calendarSelected: selectedCalendarName,
	};
	const payload = {
		calendars: [newCalendar],
		username,
	};
	const response = await addCalComCalendar(payload);
	return response;
};

if (window.opener) {
	// Extract any data from URL params if needed
	const urlParams = new URLSearchParams(window.location.search);
	const code = urlParams.get('code');
	const state = urlParams.get('state');

	// Get tab parameter if it exists
	const tab = urlParams.get('tab');

	window.opener.postMessage(
		{
			type: 'oauth-complete',
			source: 'gohighlevel',
			success: true,
			data: { code, state },
			tab,
		},
		'*',
	);
	window.close();
}

export default {
	checkGoHighLevelConnection,
	getGoHighLevelAuthUrl,
	processGoHighLevelCallback,
	disconnectGoHighLevel,
	createGoHighLevelOAuthState,
	fetchGhlCalendars,
	openOAuthWindow,
	createGhlCalendarWorkflow,
};
